import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Upload, X, FileImage, FileVideo, ArrowLeft, AlertCircle, CheckCircle, Lightbulb, HelpCircle, Clock, CheckCircle2, MessageSquare, History, ZoomIn, Bell } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CORRECTION_TYPES = [
  { value: "error", label: "Error", icon: AlertCircle },
  { value: "mejora", label: "Mejora", icon: Lightbulb },
  { value: "omision", label: "Omisión", icon: HelpCircle },
  { value: "otro", label: "Otro", icon: HelpCircle },
];

const PRIORITIES = [
  { value: "alta", label: "Alta" },
  { value: "media", label: "Media" },
  { value: "baja", label: "Baja" },
];

// Format date to Mexico timezone
const formatDateMexico = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleString("es-MX", {
    timeZone: "America/Mexico_City",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  });
};

// Handle paste for images
const handlePasteImages = (e, setFiles) => {
  const items = e.clipboardData?.items;
  if (!items) return;
  
  const imageFiles = [];
  for (let i = 0; i < items.length; i++) {
    if (items[i].type.startsWith('image/')) {
      const file = items[i].getAsFile();
      if (file) {
        if (file.size > 15 * 1024 * 1024) {
          toast.error("La imagen pegada excede 15MB");
          continue;
        }
        const newFile = new File([file], `captura_${Date.now()}.png`, { type: file.type });
        imageFiles.push(newFile);
      }
    }
  }
  
  if (imageFiles.length > 0) {
    setFiles(prev => [...prev, ...imageFiles]);
    toast.success(`${imageFiles.length} imagen(es) pegada(s)`);
  }
};

export default function ClientDashboard() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [corrections, setCorrections] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("recordatorios");
  
  const [form, setForm] = useState({
    title: "",
    description: "",
    correction_type: "error",
    priority: "media",
  });
  const [files, setFiles] = useState([]);

  const password = localStorage.getItem("clientPassword");
  const categoryName = localStorage.getItem("categoryName");

  const fetchCorrections = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/corrections/${categoryId}`, {
        params: { password }
      });
      setCorrections(response.data);
    } catch (error) {
      toast.error("Error al cargar correcciones");
    } finally {
      setLoading(false);
    }
  }, [categoryId, password]);

  // Load reminders from localStorage and refresh from API
  const loadReminders = useCallback(async () => {
    try {
      // First try to get from localStorage
      const storedReminders = localStorage.getItem("clientReminders");
      if (storedReminders) {
        setReminders(JSON.parse(storedReminders));
      }
      
      // Then refresh from API via client/access
      const response = await axios.post(`${API}/client/access`, { password });
      if (response.data.reminders) {
        setReminders(response.data.reminders);
        localStorage.setItem("clientReminders", JSON.stringify(response.data.reminders));
      }
    } catch (error) {
      console.error("Error loading reminders");
    }
  }, [password]);

  useEffect(() => {
    if (!password) {
      navigate("/");
      return;
    }
    fetchCorrections();
    loadReminders();
  }, [password, navigate, fetchCorrections, loadReminders]);

  // State for history dialog
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedCorrection, setSelectedCorrection] = useState(null);

  // State for image viewer
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const openImageViewer = (imageData, contentType, filename) => {
    setSelectedImage({ data: imageData, contentType, filename });
    setImageViewerOpen(true);
  };

  const openHistoryDialog = (correction) => {
    setSelectedCorrection(correction);
    setHistoryDialogOpen(true);
    // NO marcar como leído solo por ver - el indicador permanece hasta que el cliente RESPONDA
  };

  // Sort by priority: alta > media > baja
  const priorityOrder = { alta: 0, media: 1, baja: 2 };
  const sortByPriority = (a, b) => (priorityOrder[a.priority] || 1) - (priorityOrder[b.priority] || 1);
  
  // Sort unread admin replies first
  const sortUnreadFirst = (a, b) => {
    if (a.has_unread_admin_reply && !b.has_unread_admin_reply) return -1;
    if (!a.has_unread_admin_reply && b.has_unread_admin_reply) return 1;
    return sortByPriority(a, b);
  };

  // Filter corrections by status - "En Proceso" includes pendiente and retroalimentacion
  const activeCorrections = corrections.filter(c => c.status === "pendiente" || c.status === "retroalimentacion").sort(sortUnreadFirst);
  const completedCorrections = corrections.filter(c => c.status === "completado").sort(sortByPriority);

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    const validFiles = newFiles.filter(file => {
      if (file.size > 15 * 1024 * 1024) {
        toast.error(`${file.name} excede 15MB`);
        return false;
      }
      return true;
    });
    setFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      toast.error("Completa todos los campos requeridos");
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    formData.append("title", form.title);
    formData.append("description", form.description);
    formData.append("correction_type", form.correction_type);
    formData.append("priority", form.priority);
    formData.append("password", password);
    
    files.forEach(file => {
      formData.append("files", file);
    });

    try {
      await axios.post(`${API}/corrections/${categoryId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Corrección enviada exitosamente");
      setDialogOpen(false);
      setForm({ title: "", description: "", correction_type: "error", priority: "media" });
      setFiles([]);
      fetchCorrections();
    } catch (error) {
      toast.error("Error al enviar corrección");
    } finally {
      setSubmitting(false);
    }
  };

  const getTypeIcon = (type) => {
    const typeObj = CORRECTION_TYPES.find(t => t.value === type);
    return typeObj ? typeObj.icon : HelpCircle;
  };

  const getPriorityClass = (priority) => {
    const classes = {
      alta: "priority-alta",
      media: "priority-media",
      baja: "priority-baja",
    };
    return classes[priority] || "priority-media";
  };

  const renderCorrectionCard = (correction, index, isCompleted = false) => {
    const TypeIcon = getTypeIcon(correction.correction_type);
    
    return (
      <Card 
        key={correction.id} 
        className={`bg-card border-border card-hover animate-fade-in relative ${isCompleted ? 'border-green-800/50' : ''}`}
        style={{ animationDelay: `${index * 0.05}s` }}
        data-testid={`correction-card-${correction.id}`}
      >
        {isCompleted && (
          <div className="absolute top-0 right-0 bg-green-900/90 text-green-400 px-4 py-2 rounded-bl-md rounded-tr-sm flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            <span className="text-xs uppercase font-bold tracking-wider">Resuelto</span>
          </div>
        )}
        
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-sm ${isCompleted ? 'bg-green-900/30' : 'bg-muted'}`}>
                {isCompleted ? (
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                ) : (
                  <TypeIcon className="w-4 h-4" />
                )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono bg-primary/20 text-primary px-2 py-0.5 rounded">
                    ID: {String(correction.sequential_id || 0).padStart(2, '0')}
                  </span>
                  <CardTitle className={`text-base font-semibold ${isCompleted ? 'line-through text-muted-foreground' : ''}`}>
                    {correction.title}
                  </CardTitle>
                </div>
                <p className="text-xs text-muted-foreground mono mt-1">
                  {formatDateMexico(correction.created_at)}
                </p>
              </div>
            </div>
            <div className={`flex items-center gap-2 ${isCompleted ? 'mt-6' : ''}`}>
              <Badge className={`text-xs uppercase ${getPriorityClass(correction.priority)}`}>
                {correction.priority}
              </Badge>
              <Badge className={`text-xs uppercase ${isCompleted ? 'status-completado' : 'status-pendiente'} flex items-center gap-1`}>
                {isCompleted ? (
                  <><CheckCircle2 className="w-3 h-3" /> Completado</>
                ) : (
                  <><Clock className="w-3 h-3" /> Pendiente</>
                )}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className={`text-sm whitespace-pre-wrap ${isCompleted ? 'text-muted-foreground/70' : 'text-muted-foreground'}`}>
            {correction.description}
          </p>
          
          {/* Mostrar indicador y botón de historial si hay mensajes */}
          {correction.feedback_messages && correction.feedback_messages.length > 0 && (
            <div className={`mt-4 p-3 rounded-sm ${correction.has_unread_admin_reply ? 'bg-red-900/30 border border-red-500 animate-pulse' : 'bg-purple-900/20 border border-purple-900/50'}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <MessageSquare className={`w-4 h-4 ${correction.has_unread_admin_reply ? 'text-red-400' : 'text-purple-400'}`} />
                    <span className={`text-sm ${correction.has_unread_admin_reply ? 'text-red-400' : 'text-purple-400'}`}>
                      Conversación con el equipo ({correction.feedback_messages.length} mensaje{correction.feedback_messages.length !== 1 ? 's' : ''})
                    </span>
                  </div>
                  {correction.has_unread_admin_reply && (
                    <span className="text-sm font-bold text-red-400 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" /> ¡NUEVA RESPUESTA DEL EQUIPO!
                    </span>
                  )}
                </div>
                <Button 
                  size="sm" 
                  onClick={() => openHistoryDialog(correction)}
                  className={`${correction.has_unread_admin_reply ? 'bg-red-600 hover:bg-red-700' : 'bg-purple-600 hover:bg-purple-700'} text-white`}
                >
                  <History className="w-4 h-4 mr-1" /> Ver Historial
                </Button>
              </div>
            </div>
          )}
          
          {isCompleted && (
            <div className="mt-4 p-3 bg-green-900/20 border border-green-900/50 rounded-sm">
              <p className="text-sm text-green-400 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                <span>Esta corrección ha sido revisada y completada por el equipo.</span>
              </p>
            </div>
          )}
          
          {correction.attachments && correction.attachments.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {correction.attachments.map((att, i) => (
                <div 
                  key={att.id || i}
                  className="relative group"
                >
                  {att.content_type?.startsWith("video") ? (
                    <video 
                      src={`data:${att.content_type};base64,${att.data}`}
                      className="w-24 h-24 object-cover rounded-sm border border-border"
                      controls
                    />
                  ) : (
                    <img 
                      src={`data:${att.content_type};base64,${att.data}`}
                      alt={att.filename}
                      className="w-24 h-24 object-cover rounded-sm border border-border cursor-pointer hover:border-primary"
                      onClick={() => window.open(`data:${att.content_type};base64,${att.data}`, "_blank")}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 md:p-12" data-testid="client-dashboard">
      {/* Header */}
      <header className="mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="mb-6 text-muted-foreground hover:text-foreground"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Cerrar Sesión
        </Button>
        
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold uppercase tracking-wider text-foreground mb-2">
              {categoryName || "Proyecto"}
            </h1>
            <p className="text-muted-foreground text-sm tracking-wide">
              {corrections.length} TRANSMISIONES EN TOTAL
            </p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-primary hover:bg-primary/90 uppercase tracking-wider btn-glow"
                data-testid="new-correction-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nueva Transmisión
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl uppercase tracking-wider">
                  Nueva Transmisión
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Título *
                  </label>
                  <Input
                    value={form.title}
                    onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Título breve del reporte"
                    className="bg-input border-border"
                    data-testid="correction-title-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Tipo
                    </label>
                    <Select
                      value={form.correction_type}
                      onValueChange={(value) => setForm(prev => ({ ...prev, correction_type: value }))}
                    >
                      <SelectTrigger className="bg-input border-border" data-testid="correction-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CORRECTION_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Prioridad
                    </label>
                    <Select
                      value={form.priority}
                      onValueChange={(value) => setForm(prev => ({ ...prev, priority: value }))}
                    >
                      <SelectTrigger className="bg-input border-border" data-testid="correction-priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIORITIES.map(p => (
                          <SelectItem key={p.value} value={p.value}>
                            {p.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Descripción *
                  </label>
                  <Textarea
                    value={form.description}
                    onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                    onPaste={(e) => handlePasteImages(e, setFiles)}
                    placeholder="Describe detalladamente el error... (Ctrl+V para pegar imágenes)"
                    className="bg-input border-border min-h-[120px]"
                    data-testid="correction-description-input"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Archivos Adjuntos (máx. 15MB c/u)
                  </label>
                  <div className="drop-zone cursor-pointer" onClick={() => document.getElementById("file-input").click()}>
                    <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      Haz clic para adjuntar o <strong>Ctrl+V</strong> para pegar
                    </p>
                    <input
                      id="file-input"
                      type="file"
                      multiple
                      accept="image/*,video/*"
                      onChange={handleFileChange}
                      className="hidden"
                      data-testid="file-input"
                    />
                  </div>

                  {files.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {files.map((file, index) => (
                        <div key={index} className="flex items-center gap-2 bg-muted px-3 py-1.5 rounded-sm text-sm">
                          {file.type.startsWith("video") ? (
                            <FileVideo className="w-4 h-4" />
                          ) : (
                            <FileImage className="w-4 h-4" />
                          )}
                          <span className="max-w-[150px] truncate">{file.name}</span>
                          <button type="button" onClick={() => removeFile(index)}>
                            <X className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-primary hover:bg-primary/90 uppercase tracking-wider"
                  data-testid="submit-correction-button"
                >
                  {submitting ? "Enviando..." : "Enviar Transmisión"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      {/* Tabs: Recordatorios, En Proceso, Completadas */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-2xl grid-cols-3 mb-8 bg-secondary/50">
          <TabsTrigger 
            value="recordatorios" 
            className={`data-[state=active]:bg-yellow-900/50 data-[state=active]:text-yellow-400 uppercase tracking-wider text-sm ${reminders.length > 0 ? 'animate-pulse' : ''}`}
            data-testid="tab-recordatorios"
          >
            <Bell className="w-4 h-4 mr-2" />
            Recordatorios
            <Badge className={`ml-2 ${reminders.length > 0 ? 'bg-yellow-600 text-white border-yellow-500' : 'bg-yellow-900/50 text-yellow-400 border-yellow-900'}`}>
              {reminders.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger 
            value="pendientes" 
            className="data-[state=active]:bg-orange-900/50 data-[state=active]:text-orange-400 uppercase tracking-wider text-sm"
            data-testid="tab-pendientes"
          >
            <Clock className="w-4 h-4 mr-2" />
            En Proceso
            <Badge className="ml-2 bg-orange-900/50 text-orange-400 border-orange-900">
              {activeCorrections.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger 
            value="completadas" 
            className="data-[state=active]:bg-green-900/50 data-[state=active]:text-green-400 uppercase tracking-wider text-sm"
            data-testid="tab-completadas"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Completadas
            <Badge className="ml-2 bg-green-900/50 text-green-400 border-green-900">
              {completedCorrections.length}
            </Badge>
          </TabsTrigger>
        </TabsList>

        {/* Tab: Recordatorios */}
        <TabsContent value="recordatorios">
          {reminders.length === 0 ? (
            <div className="text-center py-20">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-md bg-card border border-border mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">¡Sin recordatorios!</h3>
              <p className="text-muted-foreground text-sm">
                No hay instrucciones pendientes del equipo
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {reminders.map((reminder, index) => (
                <Card key={reminder.id || index} className="bg-yellow-900/20 border-yellow-600/50 animate-pulse">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-md bg-yellow-600/30">
                        <Bell className="w-6 h-6 text-yellow-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-lg text-yellow-100 font-medium">{reminder.instruction}</p>
                        <p className="text-xs text-yellow-400/70 mt-2">
                          Agregado: {formatDateMexico(reminder.created_at)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="pendientes">
          {activeCorrections.length === 0 ? (
            <div className="text-center py-20">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-md bg-card border border-border mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">¡Sin tareas pendientes!</h3>
              <p className="text-muted-foreground text-sm">
                Todas las correcciones han sido atendidas
              </p>
            </div>
          ) : (
            <div className="grid gap-4">
              {activeCorrections.map((correction, index) => 
                renderCorrectionCard(correction, index, false)
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="completadas">
          {completedCorrections.length === 0 ? (
            <div className="text-center py-20">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-md bg-card border border-border mb-4">
                <Clock className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Sin correcciones completadas</h3>
              <p className="text-muted-foreground text-sm">
                Las correcciones completadas aparecerán aquí
              </p>
            </div>
          ) : (
            <div className="grid gap-4">
              {completedCorrections.map((correction, index) => 
                renderCorrectionCard(correction, index, true)
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl uppercase tracking-wider flex items-center gap-2">
              <History className="w-5 h-5 text-purple-400" />
              Historial de Conversación
            </DialogTitle>
          </DialogHeader>
          
          {selectedCorrection && (
            <div className="flex-1 flex flex-col overflow-hidden mt-4">
              {/* Correction info */}
              <div className="bg-muted/30 rounded-sm p-3 mb-4 flex-shrink-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono bg-primary/20 text-primary px-2 py-0.5 rounded">
                    ID: {String(selectedCorrection.sequential_id || 0).padStart(2, '0')}
                  </span>
                  <span className="font-semibold">{selectedCorrection.title}</span>
                </div>
                <p className="text-xs text-muted-foreground">{selectedCorrection.description?.substring(0, 100)}...</p>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto space-y-4 pr-2">
                {selectedCorrection.feedback_messages?.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No hay mensajes en esta conversación</p>
                  </div>
                ) : (
                  selectedCorrection.feedback_messages?.map((msg, index) => (
                    <div key={msg.id || index} className={`flex ${msg.sender === "client" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] ${msg.sender === "client" ? "bg-green-900/20 border-r-2 border-r-green-500" : "bg-primary/20 border-l-2 border-l-primary"} rounded-md p-3`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-semibold uppercase">
                            {msg.sender === "client" ? "Tú" : "Equipo"}
                          </span>
                          <span className="text-xs text-muted-foreground mono">
                            {formatDateMexico(msg.created_at)}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                        {msg.attachments?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {msg.attachments.map((att, i) => (
                              <div 
                                key={i}
                                className="relative group cursor-pointer"
                                onClick={() => openImageViewer(att.data, att.content_type, att.filename)}
                              >
                                <img 
                                  src={`data:${att.content_type};base64,${att.data}`} 
                                  alt={att.filename}
                                  className="w-20 h-20 object-cover rounded-sm border border-border"
                                />
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-sm">
                                  <ZoomIn className="w-6 h-6 text-white" />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Footer note */}
              <div className="flex-shrink-0 border-t border-border pt-4 mt-4">
                <p className="text-xs text-muted-foreground text-center">
                  Este es el historial completo de la conversación con el equipo de soporte.
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Viewer Dialog */}
      <Dialog open={imageViewerOpen} onOpenChange={setImageViewerOpen}>
        <DialogContent className="bg-card border-border max-w-4xl max-h-[90vh] p-2">
          <DialogHeader className="p-2">
            <DialogTitle className="text-sm text-muted-foreground">
              {selectedImage?.filename || "Imagen"}
            </DialogTitle>
          </DialogHeader>
          {selectedImage && (
            <div className="flex items-center justify-center overflow-auto max-h-[80vh]">
              <img 
                src={`data:${selectedImage.contentType};base64,${selectedImage.data}`}
                alt={selectedImage.filename}
                className="max-w-full max-h-[75vh] object-contain rounded-sm"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
